import clsx from "clsx";
import { twMerge } from "tailwind-merge";

// Round Button ----------------------------------------------------------------

interface RoundButtonProps extends React.ComponentProps<"button"> {
  variant?: "default" | "secondary" | "success" | "danger";
}
const roundButtonVariants = {
  default: [
    "text-white bg-blue-500 outline-blue-500 hover:enabled:bg-blue-600 shadow-blue-500/40",
    "dark:text-neutral-900 dark:hover:enabled:bg-blue-400",
  ],
  secondary: [
    "text-neutral-800 bg-white outline-neutral-500 hover:enabled:bg-neutral-100",
    "border border-neutral-200 dark:border-neutral-700",
    "dark:text-neutral-200 dark:bg-neutral-800 dark:hover:enabled:bg-neutral-700",
  ],
  success: [
    "text-white bg-green-600 outline-green-600 hover:enabled:bg-green-700 shadow-green-600/40",
    "dark:text-neutral-900 dark:hover:enabled:bg-green-500",
  ],
  danger: [
    "text-white bg-red-500 outline-red-500 hover:enabled:bg-red-600 shadow-red-500/40",
    "dark:text-neutral-900 dark:hover:enabled:bg-red-400",
  ],
} as const;

export function RoundButton({
  children,
  className,
  variant = "default",
  ...props
}: RoundButtonProps) {
  return (
    <button
      className={twMerge(
        clsx(
          "rounded-3xl font-semibold px-5 py-2.5 cursor-pointer transition-all shadow-sm hover:shadow-md",
          "focus-visible:outline-2 focus-visible:outline-offset-2",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          roundButtonVariants[variant],
        ),
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

// Outline Button --------------------------------------------------------------

interface OutlineButtonProps extends React.ComponentProps<"button"> {
  variant?: "default" | "secondary" | "success" | "danger";
  small?: boolean;
}

const outlineButtonVariants = {
  default: [
    "text-white bg-blue-500 outline-blue-500 hover:enabled:bg-blue-600",
    "border-blue-600 dark:border-blue-400",
    "dark:text-neutral-900 dark:hover:enabled:bg-blue-400",
  ],
  secondary: [
    "text-neutral-800 bg-white outline-neutral-500 hover:enabled:bg-neutral-100",
    "border border-neutral-200 dark:border-neutral-700",
    "dark:text-neutral-200 dark:bg-neutral-800 dark:hover:enabled:bg-neutral-700",
  ],
  success: [
    "text-white bg-green-600 outline-green-600 hover:enabled:bg-green-700",
    "border-green-800",
    "dark:text-neutral-900 dark:hover:enabled:bg-green-500",
  ],
  danger: [
    "text-white bg-red-500 outline-red-500 hover:enabled:bg-red-600",
    "border-red-700",
    "dark:text-neutral-900 dark:hover:enabled:bg-red-400",
  ],
} as const;

export function OutlineButton({
  children,
  className,
  variant = "default",
  small = false,
  ...props
}: OutlineButtonProps) {
  return (
    <button
      className={twMerge(
        clsx(
          "rounded-lg font-semibold cursor-pointer transition-all",
          "border focus-visible:outline-2 focus-visible:outline-offset-2",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          outlineButtonVariants[variant],
          {
            "px-5 py-2.5": !small,
            "px-4 py-2 text-sm": small,
          },
        ),
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

// Secondary Button ------------------------------------------------------------

const secondaryButtonVariants = {
  default: [
    "text-blue-500 outline-blue-500 bg-blue-200/10 dark:bg-blue-800/10",
    "border-blue-300 dark:border-blue-900",
    "hover:enabled:bg-blue-50 dark:hover:enabled:bg-blue-800/30",
  ],
  secondary: [
    "text-neutral-600 dark:text-neutral-400 bg-neutral-50 outline-neutral-500",
    "border-neutral-300 dark:border-neutral-700",
    "hover:enabled:bg-neutral-100",
    "dark:bg-neutral-800/50 dark:hover:enabled:bg-neutral-800",
  ],
  success: [
    "text-green-600 outline-green-600 bg-green-200/10 dark:bg-green-800/10",
    "border-green-400 dark:border-green-900",
    "hover:enabled:bg-green-100/75 dark:hover:enabled:bg-green-800/30",
  ],
  danger: [
    "text-red-500 outline-red-500 bg-red-200/10 dark:bg-red-800/10",
    "border-red-300 dark:border-red-900",
    "hover:enabled:bg-red-50 dark:hover:enabled:bg-red-800/30",
  ],
} as const;

const secondaryButtonHighlights = {
  default: ["border-blue-500 dark:border-blue-500"],
  secondary: ["text-neutral-700 dark:text-neutral-300"],
  success: ["border-green-500 dark:border-green-500"],
  danger: ["border-red-500 dark:border-red-500"],
} as const;

interface SecondaryButtonProps extends OutlineButtonProps {
  highlight?: boolean;
  small?: boolean;
  round?: boolean;
}

export function SecondaryButton({
  children,
  className,
  variant = "default",
  highlight = false,
  small = false,
  round = false,
  ...props
}: SecondaryButtonProps) {
  return (
    <button
      className={twMerge(
        clsx(
          "text-sm font-semibold cursor-pointer transition-all",
          "border focus-visible:outline-2 focus-visible:outline-offset-2",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          "shadow-sm hover:shadow-md",
          {
            "px-4 py-2": !small,
            "px-3 py-1.5": small,
            "rounded-md": !round,
            "rounded-2xl": round,
          },
          secondaryButtonVariants[variant],
          highlight ? secondaryButtonHighlights[variant] : [],
        ),
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

// Label Button ----------------------------------------------------------------

interface LabelButtonProps extends React.ComponentProps<"button"> {}

export function LabelButton({ children, className, ...props }: LabelButtonProps) {
  return (
    <button
      className={twMerge(
        clsx(
          "text-neutral-500 text-xs border px-1 rounded-sm transition-all",
          "hover:text-blue-500 py-px cursor-pointer font-medium",
        ),
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
